import React from 'react';
import { css } from '@emotion/react';
import { QUERY_REQUEST_DATA, MUTATION_UPDATE_DATA_TIMES } from 'queries/queries-booking';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'urql';
import { Button } from '../../../components/Button';
import { FormControl } from '../../../components/FormControl';
import { FormFooter } from '../../../components/FormFooter';
import { FormLine } from '../../../components/FormLine';
import { FormSection } from '../../../components/FormSection';
import { Heading } from '../../../components/Heading';
import { Input } from '../../../components/Input';
import { useStep } from '../hooks/useStep';
import { SpinnerBlock } from 'components/SpinnerBlock';
import { timeValidation } from '../forms/validations';
import { format } from 'date-fns';
import { transformTime } from 'utils/utils';
import { useStore } from 'hooks/useStore';
import { theme } from 'styles/theme';
import { OptionalInfoBox } from '../components/OptionalInfoBox';
import { useRequestStateQuery } from 'generated/graphql';

enum FORM_TIMES {
	ARRIVAL = 'time_arrival',
	PODIUM_ACCESS = 'time_podium_access',
	SOUND_TEST = 'time_sound_test',
	DINNER = 'time_dinner',
	HALL_OPENING = 'time_hall_opening',
	PERFORMANCE = 'time_performance',
}

export const TimesStep = () => {
	const { id } = useStep();

	const [queryRequestData] = useQuery({
		query: QUERY_REQUEST_DATA,
		variables: {
			url: id,
		},
	});

	const [_stateRequest] = useRequestStateQuery({
		variables: { url: id },
	});

	const timeUpdateAt = queryRequestData?.data?.request?.time_update_at;

	return (
		<div
			css={css`
				width: 560px;
			`}
		>
			<Heading
				title="Časy vystoupení"
				desc={
					<>
						<OptionalInfoBox />
						{!!timeUpdateAt && queryRequestData?.data?.request?.confirmed && (
							<div
								css={css`
									border: 1px solid #f1f1f4;
									border-radius: 12px;
									background: #fafaff;
									padding: 12px 20px;
									margin-top: 8px;
									text-align: center;
									font-size: 14px;
									line-height: 20px;
									color: ${theme.color.text['400']};
								`}
							>
								<strong
									css={css`
										font-weight: 500;
									`}
								>
									Poslední aktualizace:
								</strong>{' '}
								{format(new Date(timeUpdateAt), 'dd. MM. YYY, HH:mm')}
							</div>
						)}
					</>
				}
			/>

			{queryRequestData.fetching ? (
				<SpinnerBlock />
			) : (
				<Form defaultValues={queryRequestData?.data?.request} url={id} />
			)}
		</div>
	);
};

const Form = ({ defaultValues, url }) => {
	const { notifications } = useStore();

	const { goNextStep, goPrevStep } = useStep();

	const localDefaultValues = {
		time_arrival: transformTime(defaultValues?.time_arrival),
		time_podium_access: transformTime(defaultValues?.time_podium_access),
		time_sound_test: transformTime(defaultValues?.time_sound_test),
		time_dinner: transformTime(defaultValues?.time_dinner),
		time_hall_opening: transformTime(defaultValues?.time_hall_opening),
		time_performance: transformTime(defaultValues?.time_performance),
	};

	const {
		handleSubmit,
		register,
		formState: { errors },
	} = useForm({ defaultValues: localDefaultValues });

	// Submit Form
	const [updateDataTimes, executeUpdateDataTimes] = useMutation(MUTATION_UPDATE_DATA_TIMES);

	const onSubmit = (data: any) => {
		// console.log('SUBMIT');
		// console.log(data);

		executeUpdateDataTimes({
			url: url,
			input: data,
		}).then((result) => {
			// console.log(result);

			if (result?.error) {
				// VYHODIT ERROR
				return false;
			} else {
				notifications.addNotification({ message: 'Uloženo' });
			}

			goNextStep();
		});
	};

	const labelWidth = 200;
	const inputWidth = 80;

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<FormSection>
				<FormLine first>
					<FormControl
						label="Příjezd"
						htmlFor={FORM_TIMES.ARRIVAL}
						labelWidth={labelWidth}
						error={errors[FORM_TIMES.ARRIVAL]}
						alignRight
					>
						<Input
							placeholder="00:00"
							{...register(FORM_TIMES.ARRIVAL, timeValidation())}
							error={!!errors[FORM_TIMES.ARRIVAL]}
							width={inputWidth}
							alignRight
						/>
					</FormControl>
				</FormLine>
				<FormLine>
					<FormControl
						label="Přístup na podium"
						htmlFor={FORM_TIMES.PODIUM_ACCESS}
						labelWidth={labelWidth}
						error={errors[FORM_TIMES.PODIUM_ACCESS]}
						alignRight
					>
						<Input
							placeholder="00:00"
							{...register(FORM_TIMES.PODIUM_ACCESS, timeValidation())}
							error={!!errors[FORM_TIMES.PODIUM_ACCESS]}
							width={inputWidth}
							alignRight
						/>
					</FormControl>
				</FormLine>
				<FormLine>
					<FormControl
						label="Zvuková zkouška"
						htmlFor={FORM_TIMES.SOUND_TEST}
						labelWidth={labelWidth}
						error={errors[FORM_TIMES.SOUND_TEST]}
						alignRight
					>
						<Input
							placeholder="00:00"
							{...register(FORM_TIMES.SOUND_TEST, timeValidation())}
							error={!!errors[FORM_TIMES.SOUND_TEST]}
							width={inputWidth}
							alignRight
						/>
					</FormControl>
				</FormLine>
				<FormLine>
					<FormControl
						label="Večeře"
						htmlFor={FORM_TIMES.DINNER}
						labelWidth={labelWidth}
						error={errors[FORM_TIMES.DINNER]}
						alignRight
					>
						<Input
							placeholder="00:00"
							{...register(FORM_TIMES.DINNER, timeValidation())}
							error={!!errors[FORM_TIMES.DINNER]}
							width={inputWidth}
							alignRight
						/>
					</FormControl>
				</FormLine>
				<FormLine>
					<FormControl
						label="Otevření sálu"
						htmlFor={FORM_TIMES.HALL_OPENING}
						labelWidth={labelWidth}
						error={errors[FORM_TIMES.HALL_OPENING]}
						alignRight
					>
						<Input
							placeholder="00:00"
							{...register(FORM_TIMES.HALL_OPENING, timeValidation())}
							error={!!errors[FORM_TIMES.HALL_OPENING]}
							width={inputWidth}
							alignRight
						/>
					</FormControl>
				</FormLine>
				<FormLine>
					<FormControl
						label="Čas vystoupení"
						htmlFor={FORM_TIMES.PERFORMANCE}
						labelWidth={labelWidth}
						error={errors[FORM_TIMES.PERFORMANCE]}
						alignRight
					>
						<Input
							placeholder="00:00"
							{...register(FORM_TIMES.PERFORMANCE, timeValidation())}
							error={!!errors[FORM_TIMES.PERFORMANCE]}
							width={inputWidth}
							alignRight
						/>
					</FormControl>
				</FormLine>
			</FormSection>

			<FormFooter>
				<Button outline type="button" onClick={() => goPrevStep()}>
					Zpět
				</Button>
				<Button type="submit" isProcessing={updateDataTimes.fetching}>
					{defaultValues?.confirmed ? 'Aktualizovat a pokračovat' : 'Uložit a pokračovat'}
				</Button>
			</FormFooter>
		</form>
	);
};
